import React from "react";
import useStore from "@/store/store";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/toast/CustomToast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { CurDate } from "@store/scheduleStore.ts";
import { useIsAdmin } from "@/hooks/useAuth";
import type { WantedComplianceTotals } from "@/utils/wanted-compliance";

/**
 * 원티드 준수 요약 칩 — 토글을 켜기 전에도 보인다.
 * 예전엔 토글을 켜야만 데이터가 왔기 때문에 "이 달에 금지근무가 걸려 있는지"조차
 * 알 수 없었다. 문제(미반영·위반)가 있을 때만 빨강, 아니면 조용한 회색.
 */
function WantedComplianceChip({
  label,
  total,
  bad,
  badLabel,
  okLabel,
  badNames,
}: {
  readonly label: string;
  readonly total: number;
  readonly bad: number;
  readonly badLabel: string;
  /** 정상일 때 쓰는 말. 고정 원티드는 "반영", 금지근무는 "준수"로 방향이 반대다. */
  readonly okLabel: string;
  readonly badNames: readonly string[];
}) {
  if (total <= 0) return null;

  const NAME_LIMIT = 8;
  const shown = badNames.slice(0, NAME_LIMIT).join(", ");
  const rest = badNames.length - NAME_LIMIT;
  const tooltip =
    bad > 0
      ? `${label} ${total}건 중 ${badLabel} ${bad}건` +
        (shown ? `\n${badLabel}: ${shown}${rest > 0 ? ` 외 ${rest}명` : ""}` : "")
      : `${label} ${total}건 전부 ${okLabel}`;

  return (
    <span
      className={cn(
        "inline-flex h-7 shrink-0 items-center gap-1 rounded-full border px-2.5",
        "text-xs font-semibold tabular-nums",
        bad > 0
          ? "border-red-300 bg-red-50 text-red-700"
          : "border-slate-200 bg-slate-50 text-slate-500"
      )}
      title={tooltip}
    >
      <span className="whitespace-nowrap">
        {label} {total}
      </span>
      {bad > 0 ? (
        <span className="whitespace-nowrap">
          · {badLabel} {bad}
        </span>
      ) : null}
    </span>
  );
}

export type RosterViewSettingsProps = {
  /** 전체보기(0) / 팀별보기(1) */
  currentTeamTableViewTab: number;
  setCurrentTeamTableViewTab: React.Dispatch<React.SetStateAction<number>>;
  /** 원티드 반영사항 로딩 중이면 토글·뷰 전환 비활성화 (ButtonGroup과 동일 정책) */
  isWantedAdjustmentLoading: boolean;
  /** 전월 말일 이어보기 */
  isPrevMonthOpen: boolean;
  setIsPrevMonthOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isFetchingPrevTail: boolean;
  curDate: CurDate | null;
  /** 인력 제약 검증을 끄는 화면이면 최소인원 스위치 숨김 (RosterMadeTable과 동일) */
  hideManpowerViolationToggle?: boolean;
  hideViewModeToggle?: boolean;
  variant?: "section" | "inline";
  /** 그룹 전체 원티드 준수 합계. 표시 토글과 무관하게 항상 보여 준다. */
  wantedCompliance?: WantedComplianceTotals | null;
};

function SettingToggleButton({
  label,
  checked,
  onCheckedChange,
  disabled,
  tooltip,
  busy,
  busyLabel,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  readonly tooltip?: string;
  readonly busy?: boolean;
  readonly busyLabel?: string;
}) {
  const control = (
    <button
      type="button"
      aria-pressed={checked}
      aria-label={label}
      disabled={disabled || busy}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "inline-flex h-7 shrink-0 items-center justify-center gap-2 rounded-full border px-3 text-xs font-semibold shadow-sm transition-colors",
        checked
          ? "border-[#0E59C6] bg-[#EFF6FF] text-[#0E59C6]"
          : "border-slate-200 bg-white text-slate-600",
        "hover:border-[#0E59C6]/40 hover:bg-[#F5F8FC]",
        "disabled:cursor-not-allowed disabled:opacity-60",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
      )}
    >
      {busy ? (
        <Loader2
          className="size-3.5 animate-spin text-[#0E59C6]"
          aria-hidden
        />
      ) : null}
      <span className="whitespace-nowrap">
        {busy && busyLabel ? busyLabel : label}
      </span>
    </button>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {control}
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return control;
}

/**
 * 근무표 작성 화면 — 보기 설정 (근무표 인력 설정과 동일한 pill 토글 행).
 * 상호 배제는 store `violation-visibility-slice`와 동일:
 * - 원티드 ON → 설정·최소인원 OFF
 * - 설정 또는 최소인원 ON → 원티드 OFF
 */
function RosterViewSettings({
  currentTeamTableViewTab,
  setCurrentTeamTableViewTab,
  isWantedAdjustmentLoading,
  isPrevMonthOpen,
  setIsPrevMonthOpen,
  isFetchingPrevTail,
  curDate,
  hideManpowerViolationToggle = false,
  hideViewModeToggle = false,
  variant = "section",
  wantedCompliance = null,
}: RosterViewSettingsProps) {
  const isAdmin = useIsAdmin();

  const {
    showManpowerViolations,
    showConfigViolations,
    showWantedViolations,
    toggleManpowerViolations,
    toggleConfigViolations,
    toggleWantedViolations,
  } = useStore();

  const violationDisabled = isWantedAdjustmentLoading;
  const viewModeDisabled = isWantedAdjustmentLoading;
  const prevMonthDisabled =
    isWantedAdjustmentLoading || !curDate?.year || !curDate?.month;

  const violationControls = (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <SettingToggleButton
        label="설정 위반사항"
        checked={showConfigViolations}
        onCheckedChange={(checked) => {
          if (checked !== showConfigViolations) toggleConfigViolations();
        }}
        disabled={violationDisabled}
      />

      {!hideManpowerViolationToggle ? (
        <SettingToggleButton
          label="최소인원 위반사항"
          checked={showManpowerViolations}
          onCheckedChange={(checked) => {
            if (checked !== showManpowerViolations) toggleManpowerViolations();
          }}
          disabled={violationDisabled}
        />
      ) : null}
    </div>
  );

  const viewControls = (
    <div className="flex min-w-0 flex-wrap items-center gap-3">
      {!isAdmin && (
        <SettingToggleButton
          label="원티드 반영사항"
          checked={showWantedViolations}
          onCheckedChange={(checked) => {
            if (checked !== showWantedViolations) toggleWantedViolations();
          }}
          disabled={violationDisabled}
        />
      )}

      {!isAdmin && wantedCompliance && (
        <>
          <WantedComplianceChip
            label="원티드"
            total={wantedCompliance.wantedOn}
            bad={wantedCompliance.wantedMissed}
            badLabel="미반영"
            okLabel="반영됨"
            badNames={wantedCompliance.wantedMissedNames}
          />
          <WantedComplianceChip
            label="금지"
            total={wantedCompliance.bannedOn}
            bad={wantedCompliance.bannedViolated}
            badLabel="위반"
            okLabel="지켜짐"
            badNames={wantedCompliance.bannedViolatedNames}
          />
        </>
      )}

      {!isAdmin && (
        <div className="h-5 w-px shrink-0 bg-slate-200" aria-hidden />
      )}

      <SettingToggleButton
        label="전월 근무 이어보기"
        checked={isPrevMonthOpen}
        onCheckedChange={(checked) => {
          if (!curDate?.year || !curDate?.month) {
            toast.error("날짜를 선택해주세요.");
            return;
          }
          setIsPrevMonthOpen(checked);
        }}
        disabled={prevMonthDisabled}
        busy={isFetchingPrevTail}
        busyLabel="전월 근무를 가져오는 중입니다"
        tooltip="전월 말일 근무표 영역 표시"
      />

      {!hideViewModeToggle && (
        <>
          <div className="h-5 w-px shrink-0 bg-slate-200" aria-hidden />

          <SettingToggleButton
            label={currentTeamTableViewTab === 0 ? "팀별보기" : "전체보기"}
            checked={currentTeamTableViewTab === 1}
            onCheckedChange={(checked) => {
              setCurrentTeamTableViewTab(checked ? 1 : 0);
            }}
            disabled={viewModeDisabled}
            tooltip="전체보기는 기본값, 팀별보기일 때만 강조"
          />
        </>
      )}
    </div>
  );

  const controls = (
    <div className="flex min-w-0 flex-wrap items-center justify-end gap-3">
      {violationControls}
      <div className="h-5 w-px shrink-0 bg-slate-200" aria-hidden />
      {viewControls}
    </div>
  );

  if (variant === "inline") {
    return (
      <div className="contents" aria-label="보기 설정">
        {controls}
      </div>
    );
  }

  return (
    <section
      className="mb-3 flex w-full flex-col gap-2 rounded-lg border border-zinc-200 bg-[#FAF9F9] px-4 py-2"
      aria-label="보기 설정"
    >
      <h2 className="shrink-0 text-sm font-semibold text-zinc-700">
        보기 설정
      </h2>
      {controls}
    </section>
  );
}

export default React.memo(RosterViewSettings);
